import { Root } from "react-dom/client"
import { createDependencyContext } from "app-base/web-shell/context"
import { useContext, useMemo, useRef, useState, ReactNode } from "react"
import { AppData } from "./App/AppData"
import { Session } from "./App/Session"
import { createApiClient } from "./App/ApiClient"
import { format } from "./App/Format"
import { Localization, LanguageTextReplacements, LanguageTextReplacementFunction } from "./App/Localization"
import { Platform } from "./App/Platform"
import { Native } from "./App/Native"
import { serverAppURL } from "./App/ServerURL"
import { openBrowserServerWindow } from "./App/BrowserServerWindow"
import { GetRoute, SetRoute, AppRoute } from "./App/Routes"
import { resolveFeature } from "./App/FeatureFlags"
import { useAppState, useAppEffects } from "./App/useAppState"
import { IAccount, IAccountUserLevel, IAppState, ILogbookAppStatusResult, ICalculated, ICalculationResult, ILogbookModel, IModelCalculations, INotable, INote, INoteAttachment, ILogbookSessionType, ILogbookSession, ISessionResponse, IUser, AccountApi } from "./Types"
import { Loading } from "./Components/Loading"
import { OpenServerDialog } from "./Components/OpenServerDialog"
import { RootView } from "./Views/RootView"
import { AppViewData } from "./Views/App/Context"
import { DataStoreProvider } from "./Data/Context"
import { accountSessionIdentity, accountSessionResult } from "./Data/AccountSession"
import { useAccountData } from "./Data/useAccountData"
import { logger } from "./App/Logger"

const appContext = createDependencyContext<IAppState>("AppContext");
export const AppContext = appContext.Context;
export const GetApp = () => useContext(AppContext)!;

interface AppProps {
  children?: ReactNode;
}

export function App({ children }: AppProps) {
  const state = useAppState();
  const target = state.sessionType == ILogbookSessionType.cloud ? "server" : "app";
  const apiClient = useMemo(() => createApiClient(target), [target]);
  const identity = accountSessionIdentity(state.session, state.sessionType, target);
  const sessionIdentityRef = useRef(identity);
  sessionIdentityRef.current = identity;
  const seed = state.accountSessionResponse
    ? accountSessionResult(state.accountSessionResponse).entities
    : undefined;

  return (
    <DataStoreProvider
      key={identity}
      socket={state.socket}
      apiClient={apiClient}
      seed={seed}
    >
      <AppContent state={state} apiClient={apiClient} sessionIdentityRef={sessionIdentityRef}>{children}</AppContent>
    </DataStoreProvider>
  );
}

interface AppContentProps extends AppProps {
  state: ReturnType<typeof useAppState>;
  apiClient: IAppState["apiClient"];
  sessionIdentityRef: { current: string };
}

function AppContent({ state, apiClient, sessionIdentityRef, children }: AppContentProps) {
  const serverOpening = useRef(false);
  const [serverNavigation, setServerNavigation] = useState<{ path: string; pending: boolean }>();
  const {
    lang, accountSessionResponse,
    appStyles, current, features, session, sessionType,
    shipId, status, socket, theme, user,
    setLang, setCurrent, setFeatures, setLocation,
    setAccountSessionResponse, setSession, setSessionType, setShipId, setStatus, setTheme, setUser,
  } = state;
  const { account, accountUserLevel } = useAccountData({
    session,
    response: accountSessionResponse,
  });
  const app: IAppState = useMemo(() => ({
    account: account,
    accountUserLevel: accountUserLevel,
    apiClient,
    appStyles: appStyles,
    current: current,
    editable: sessionType == ILogbookSessionType.primary || sessionType == ILogbookSessionType.local,
    session: session,
    sessionType: sessionType,
    shipId: shipId,
    socket: socket,
    status: status,
    theme: theme,
    user: user,
    language: lang,
    t: (text: string, replace?: LanguageTextReplacements): string => {
      return Localization.t(lang, text, replace);
    },
    getT: (prefix: string): LanguageTextReplacementFunction => {
      return (text: string, replace?: LanguageTextReplacements) => {
        return app.t([prefix, text].join("_"), replace);
      };
    },
    format: format,
    updateData: <T extends ILogbookModel,>(data: T[], object: T, resetFields?: string[]): typeof data => {
      return AppData.updateData(data, object, resetFields);
    },
    dataWithCalculations: <T extends ILogbookModel & ICalculated<C> & INotable, C extends IModelCalculations>(data: T[], calculations?: ICalculationResult<C>[], notes?: INote[], attachments?: INoteAttachment[], id?: (note: INote) => number | undefined): typeof data => {
      return AppData.dataWithCalculations(data, calculations, notes, attachments, id);
    },
    logout: async (deleteSession: boolean = true) => {
      if (deleteSession) {
        await AppData.deleteSession(app.apiClient);
      }
      Session.clearSession();
      setShipId(undefined);
      setSession({ "authorized": false });
      setAccountSessionResponse(undefined);
      setUser(undefined);
      const redirect = Session.resolveLogoutRedirect(sessionType === undefined ? undefined : app.localSession());
      if (redirect.action == "reload") {
        window.location.reload();
      } else if (redirect.action == "redirect") {
        window.location.href = redirect.url;
      }
    },
    setSession: (session: ILogbookSession, type: ILogbookSessionType, account?: IAccount, user?: IUser, features?: string[], accountUserLevel?: IAccountUserLevel) => {
      if (session.authorized) {
        const { shipId } = Session.persistSession(socket, session, type);
        setShipId(shipId);
        setSessionType(type);
        setSession(session);
        if (features) {
          setFeatures(features);
        }
        setAccountSessionResponse({ account, session, type, user, features, accountUserLevel });
        setUser(user);
      } else {
        app.logout(false);
      }
    },
    reconcileSession: (response: ISessionResponse, expectedIdentity?: string) => {
      if (expectedIdentity && sessionIdentityRef.current !== expectedIdentity) {
        return;
      }
      if (!response.session.authorized) {
        app.logout(false);
        return;
      }
      const responseTarget = response.type == ILogbookSessionType.cloud ? "server" : "app";
      const responseIdentity = accountSessionIdentity(response.session, response.type, responseTarget);
      if (sessionIdentityRef.current !== responseIdentity) {
        return;
      }
      const sameIdentity = accountSessionIdentity(session, sessionType, app.apiClient.target) === responseIdentity;
      setSessionType(response.type);
      setSession(response.session);
      if (response.features) {
        setFeatures(response.features);
      }
      setAccountSessionResponse(response);
      if (response.user || !sameIdentity) {
        setUser(response.user);
      }
    },
    updateSession: (account?: IAccount, user?: IUser) => {
      if (user) {
        setUser(user);
      }
      if (session && sessionType) {
        setAccountSessionResponse({
          account: account ?? app.account,
          session,
          type: sessionType,
          user: user ?? app.user,
          features,
          accountUserLevel: app.accountUserLevel,
        });
      }
    },
    setShipId: async (shipId: number, close?: boolean, skipPost?: boolean) => {
      if (sessionType == "primary" && skipPost != true) {
        // Let the API error through untouched. This used to rethrow a bare
        // `new Error("Failed to set ship")`, which discarded the body and status both
        // callers route on: `AppView.selectShip` needs the error name (`sessionExpired`
        // opens the re-login prompt, #1449; `limitActiveShipsReached` shows the
        // specific message), and `AppView.resetShip` reports `error.httpStatus` in the
        // close-failed dialog (#1279). Wrapping made every one of those unreachable.
        await AppData.selectShip(app.apiClient, shipId, close ? true : false);
      }
      setStatus(undefined);
      setCurrent(undefined);
      Session.persistShipId(shipId, close);
      setShipId(close ? undefined : shipId);
    },
    forceCloseShip: async (shipId: number) => {
      if (sessionType != "primary") {
        throw new Error("Force close requires a primary session");
      }
      await AppData.selectShip(app.apiClient, shipId, true, true);
      setStatus(undefined);
      setCurrent(undefined);
      Session.persistShipId(shipId, true);
      setShipId(undefined);
    },
    setLanguage: (language: string) => {
      app.language = language;
      setLang(language);
      localStorage.setItem("logbook-language", language);
      document.documentElement.setAttribute("lang", language);
      // In cloud sessions the language is persisted server-side so it follows
      // the user across devices and drives their emails. Fire-and-forget: the
      // local state is already updated above.
      const updateLanguage = new AccountApi(app.apiClient).account.updateLanguage({ params: {}, body: { language } });
      const apiUser = user ? AppViewData.apiUser({ accountId: account?.id, accountUserLevel }) : undefined;
      if (sessionType == ILogbookSessionType.cloud && updateLanguage.available(apiUser)) {
        const requestIdentity = sessionIdentityRef.current;
        updateLanguage.call()
          .then((response) => {
            if (sessionIdentityRef.current === requestIdentity) {
              app.reconcileSession(response, requestIdentity);
            }
          })
          .catch((error) => logger.debug("failed to persist language", error));
      }
    },
    setTheme: (t: string) => {
      // Prevent theme change in cloud session
      if (sessionType == ILogbookSessionType.cloud) {
        return;
      }
      app.theme = t;
      setTheme(t);
      localStorage.setItem("logbook-theme", t);
      document.documentElement.setAttribute("data-theme", t);
    },
    resetStatus: async (): Promise<void> => {
      setStatus(undefined);
      // Absorbed, never rethrown: every call site is fire-and-forget (`Focus/Frame.ts`'s
      // reset, `Trip/EditView`'s start, `useAppState`'s callbacks) and runs from a
      // subscription handler or effect that cannot await, so a rejection here becomes an
      // UNHANDLED rejection. Selecting a ship wipes and re-downloads the local DB, and
      // the app answers 503 while that is in flight (#1279) -- exactly when these fire.
      // A best-effort refresh that fails simply leaves the data as it was.
      try {
        const newStatus = await app.updateStatus();
        if (newStatus) {
          setStatus(newStatus);
        }
      } catch (error) {
        logger.debug("status refresh failed", error);
      }
    },
    updateStatus: async (): Promise<ILogbookAppStatusResult> => {
      return await AppData.loadStatus(app.apiClient);
    },
    cloudSession: (): boolean => sessionType == ILogbookSessionType.cloud,
    primarySession: (): boolean => sessionType == ILogbookSessionType.primary,
    localSession: (): boolean => sessionType == ILogbookSessionType.local,
    hasFeature: (feature: string): boolean => {
      return resolveFeature(features, feature);
    },
    updateCurrent: async () => {
      if (!session || session.authorized != true || sessionType == "cloud" || !shipId) {
        return;
      }
      // Absorbed for the same reason as `resetStatus` above -- fire-and-forget callers,
      // and a 503 is the expected answer mid-reset rather than an error worth surfacing.
      try {
        const data = await AppData.loadCurrent(app.apiClient);
        if (data) {
          setCurrent(data);
          app.current = data;
        }
      } catch (error) {
        logger.debug("current refresh failed", error);
      }
    },
    getRoute: (customRoot?: string): AppRoute => {
      return GetRoute(customRoot);
    },
    updateRoute: (path: string, replace?: boolean): void => {
      setLocation(SetRoute(path, replace));
    },
    openServer: async (path: string): Promise<boolean> => {
      if (app.cloudSession() || serverOpening.current) {
        return false;
      }
      serverOpening.current = true;
      setServerNavigation({ path, pending: true });
      try {
        const loadURL = async () => {
          const response = await AppData.loadServer(app.apiClient);
          return serverAppURL(response.host, path, Platform.isIOS(app));
        };
        if (Native.hasAppleManifest()) {
          await Native.openAppleURL(await loadURL());
        } else if (Platform.isNativeApp(app)) {
          // Legacy shells intercept this request and return no WindowProxy.
          // Keep their dispatch path separate from browser popup reservation.
          window.open(await loadURL(), "_blank");
        } else {
          await openBrowserServerWindow(loadURL);
        }
        setServerNavigation(undefined);
        return true;
      } catch (error) {
        logger.error("Cloud navigation failed", error);
        setServerNavigation({ path, pending: false });
        return false;
      } finally {
        serverOpening.current = false;
      }
    },
  }), [lang, account, accountUserLevel, apiClient, appStyles, current, features, session, sessionType, shipId, status, theme, user]);

  useAppEffects(app, state);

  return (
    <AppContext.Provider value={app}>
      <div>
        {children ? children : (session ? <RootView /> : <Loading />)}
        {serverNavigation && (
          <OpenServerDialog pending={serverNavigation.pending} t={app.getT("OpenServerDialog")}
            onClose={() => setServerNavigation(undefined)}
            onRetry={() => { void app.openServer(serverNavigation.path); }}
          />
        )}
      </div>
    </AppContext.Provider>
  );
}

// How the app answers an error that escaped React (#1813).
//
// React 19's #185 ("Maximum update depth exceeded") is thrown from
// `dispatchSetState` — in this app, from a websocket handler — so it never reaches
// a React error boundary; it surfaces on `window.onerror`. Until now that painted
// the static page below, leaving the app dead until the user reloaded by hand,
// even though the condition is transient: React's nested-update counter resets on
// the first commit that lands with an empty queue, so a fresh mount clears it.
//
// So recover, but boundedly, and record the attempts in `sessionStorage` so the
// count survives the reload step and a genuinely broken build can't reload-loop:
//
//   1st error — remount the tree in place (cheap, keeps the socket session).
//   2nd error — reload the page (heavier, but clears all module state).
//   3rd+      — give up and show the error, so a real fault stays visible.
//
// A run of clean minutes clears the count, so two unrelated errors an hour apart
// are not treated as an escalating failure.
const RECOVERY_KEY = "logbook-app-recovery";
const RECOVERY_RESET_MS = 60000;

function recoveryAttempts(): number {
  try {
    return Number(sessionStorage.getItem(RECOVERY_KEY) ?? "0") || 0;
  } catch {
    // Private-mode Safari can throw on storage access; without a count we can
    // still remount once, we just can't escalate.
    return 0;
  }
}

function recordRecoveryAttempt(attempts: number): void {
  try {
    sessionStorage.setItem(RECOVERY_KEY, String(attempts));
  } catch {
    // Ignore — see `recoveryAttempts`.
  }
}

export function clearRecoveryAttempts(): void {
  try {
    sessionStorage.removeItem(RECOVERY_KEY);
  } catch {
    // Ignore — see `recoveryAttempts`.
  }
}

export function RenderApp(root: Root) {
  // Reaching a render at all means the previous recovery worked; hold the count
  // briefly in case the error recurs immediately, then forget it.
  window.setTimeout(clearRecoveryAttempts, RECOVERY_RESET_MS);
  try {
    root.render(<App />);
  } catch (error) {
    AppError(error, root);
  }
}

export function AppError(error: unknown, root: Root) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete error type
  const err = error as any;
  const attempts = recoveryAttempts() + 1;
  recordRecoveryAttempt(attempts);
  if (attempts <= 2) {
    logger.error("app error, recovering", attempts, err?.toString?.());
    if (attempts == 1) {
      // Remounting gives React a first commit with nothing queued, which is
      // exactly what resets the nested-update counter behind #185.
      root.render(<App />);
    } else {
      window.location.reload();
    }
    return;
  }
  root.render(
    <div className="AppRenderError">
      <h1>Application Error</h1>
      <p>There was an error rendering the application:</p>
      <pre>{err.toString()}</pre>
      <details>
        <summary>Stack Trace</summary>
        <pre>{err.stack}</pre>
      </details>
    </div>,
  );
}
